import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useApiariesStore } from "entities/apiaries";
import { useHivesStore } from "entities/hives";
import { useCurrentHiveStore } from "entities/currentHive";
import { markColors } from "features/hives/ui/hivesTable/HiveMarkCell";

export type EditHiveFormValues = {
    number: string | number | null;
    uterusYear: number | null;
    uterusMark: string | number | null;
    uterusMarkColor: number;   // ← ид цвета как число
    breed: string;
};

const toColorId = (value: string | undefined | null): number => {
    if (!value) return 0;
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
    const found = markColors.find((m: any) => m.mainColor === value);
    return (found?.id ?? 0) as number;
};

export const useEditHive = () => {
    const apiaryId = useApiariesStore((s) => s.currentApiaryId);
    const getHivesFromServer = useHivesStore((s) => s.getHivesFromServer);
    const hives = useHivesStore((s) => s.hives);

    const currentHive = useCurrentHiveStore((s) => s.currentHive);
    const updateHive = useCurrentHiveStore((s) => s.updateHive);

    const defaults: EditHiveFormValues = useMemo(
        () => ({
            number: currentHive?.number ?? "",
            uterusYear: currentHive?.uterus?.year ?? new Date().getFullYear(),
            uterusMark: currentHive?.uterus?.mark ?? "",
            uterusMarkColor: toColorId(currentHive?.uterus?.color),
            breed: currentHive?.uterus?.breed ?? "",
        }),
        [currentHive]
    );

    const isReady = Boolean(currentHive);

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
        watch,
    } = useForm<EditHiveFormValues>({
        defaultValues: defaults,
        mode: "onSubmit",
    });

    useEffect(() => {
        if (isReady) reset(defaults);
    }, [defaults, reset, isReady]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const onYearChange = (y: number | null) => setValue("uterusYear", y);
    const onMarkNumberChange = (v: string) => setValue("uterusMark", v);
    const onMarkColorChange = (id: number) => setValue("uterusMarkColor", id);
    const onBreedChange = (v: string) => setValue("breed", v);

    const onSubmit = async (data: EditHiveFormValues) => {
        if (!currentHive?.id) {
            toast.error("Не найден текущий улей");
            return null;
        }
        if (!apiaryId) {
            toast.error("Не выбран точок (пасека)");
            return null;
        }

        const payload = {
            number: Number(data.number ?? currentHive.number),
            uterus: {
                year: data.uterusYear ? Number(data.uterusYear) : 0,
                mark: data.uterusMark ? Number(data.uterusMark) : 0,
                breed: data.breed?.trim() || "",
                color: String(data.uterusMarkColor), // ← преобразуем id в строку
            },
            frames: currentHive?.frames ?? 0,
            strength: currentHive?.strength ?? 0,
            brood: currentHive?.brood ?? 0,
            devices: currentHive?.devices ?? 0,
            happiness: currentHive?.happiness ?? 0,
        };

        try {
            setIsSubmitting(true);
            const res = await updateHive(payload as any);
            if (res) {
                toast.success("Изменения сохранены");
                await getHivesFromServer(
                    { apiaryId, page: { index: 0, size: Math.max(10, (hives?.length || 0)) } } as any,
                    true
                );
            } else {
                toast.error("Не удалось сохранить изменения");
            }
            return res;
        } catch {
            toast.error("Ошибка запроса. Попробуйте позже");
            return null;
        } finally {
            setIsSubmitting(false);
        }
    };

    const doReset = () => reset(defaults);

    return {
        control,
        errors,
        isSubmitting,
        onYearChange,
        onMarkNumberChange,
        onMarkColorChange,
        onBreedChange,
        handleSubmit,
        onSubmit,
        doReset,
        watch,
        isReady,
    };
};
